import { action, autorun, computed, makeObservable, observable, override, reaction, runInAction, toJS } from 'mobx';
import moment from 'moment';
import ApiFactory from '../api/ApiFactory';
import Ambiente from '../util/Ambiente';
import StorageUtil, { LOCALSTORAGE_STORAGE } from '../util/StorageUtil';
import Store from './Store';

/**
 * Classe utilizada para guardar e manipular o estado da aplicação.
 * Deve-se criar uma para cada contexto ou entidade.
 */
export default class UsuarioStore extends Store {
    // Usuário logado no sistema
    usuario;
    timerUsuarioServico;

    constructor(props) {
        super(props);

        makeObservable(this, {
            usuario: observable,
            objetoLocalStorage: computed,
            getUsuarioStorage: action,
            isUsuarioLogado: computed,
            isLoginLogus: computed,
            configurarUsuario: action,
            recuperarDetalhesUsuarioLogado: action,
            configurarPerfil: action,
            configurarLocalizacao: action,
            reset: override
        });

        this.createReaction(
            reaction(
                () => JSON.stringify(this.usuario),
                () => ApiFactory.reset(this.usuario)
            )
        );
    }

    get storageType() {
        return LOCALSTORAGE_STORAGE;
    }

    get objetoLocalStorage() {
        return {
            nomeStore: 'UsuarioStore',
            usuario: this.usuario
        };
    }

    /**
     * Recupera as informações do login anterior no LocalStorage.
     *
     * @param {string|number} cpf O cpf do usuário
     * @returns {Object} O usuário
     */
    getUsuarioStorage(cpf) {
        let storage = StorageUtil.getObject(`${this.objetoLocalStorage.nomeStore}-${cpf}`);

        if (!storage) {
            return '';
        }

        return storage['usuario'];
    }

    reset = () => {
        this.usuario = undefined;
        this.save({}); // Remove o usuário da sessão
    };

    get isUsuarioLogado() {
        return this.usuario && this.usuario.token;
    }

    get isLoginLogus() {
        return Ambiente.urls.urlLogus;
    }

    /**
     * Configura o usuário na store a partir do token gerado pelo Logus.
     *
     * @param {*} token
     * @returns
     */
    async configurarUsuario(token) {
        if (token) {
            await this.recuperarDetalhesUsuarioLogado(token);
            return this.configurarPerfil();
        }
        return false;
    }

    async recuperarDetalhesUsuarioLogado(token) {
        // Configura o token na factory para consultar os detalhes do usuário
        ApiFactory.reset({ token: token });
        let usuario = await this.loginService.recuperarDetalhesUsuarioLogado();
        usuario.token = token;
        usuario.localizacao = this.getLocalizacao(usuario);
        runInAction(() => (this.usuario = usuario));
    }

    getLocalizacao(usuario) {
        let parametros = this.rootStore.parametroStore.getParametrosIgnoreCase(usuario.localizacao);
        if (parametros.length === 0) {
            return usuario.localizacao;
        }
        if (parametros.length === 1) {
            return parametros[0].valor;
        }
        let parametro;
        let usuarioStorage = this.getUsuarioStorage(usuario.cpf);
        if (usuarioStorage) {
            // Caso haja mais de um parâmetro para a localização selecionada (SETPOESDC), tenta usar a localização que está no LocalStorage
            parametro = parametros.find((param) => param.valor === usuarioStorage.localizacao);
        }
        return parametro ? parametro.valor : parametros[0].valor;
    }

    async configurarPerfil() {
        if (!this.isUsuarioAutorizado()) {
            return false;
        }

        let ojcs = await this.loginService.buscarOrgaosJulgadoresColegiados();

        if (ojcs && ojcs.length > 0) {
            runInAction(() => (this.usuario.orgaosJulgadoresColegiados = ojcs));
            let ojcUsuario = ojcs.find((ojc) => ojc.sigla === this.usuario.localizacao);

            if (ojcUsuario) {
                if (ojcs.length > 1 && this.rootStore.appStore.tipoNavegacaoEntradaApp !== 'reload') {
                    await this.configurarLocalizacaoSessaoPadrao(ojcs);
                } else {
                    this.configurarLocalizacao(ojcUsuario);
                }

                return true;
            } else {
                autorun(() => {
                    console.error(
                        'Localização não encontrada na lista de OJCs do usuário: ' + this.usuario.localizacao
                    );
                });
            }
        }

        autorun(() => {
            console.error('Perfil inválido: ', this.usuario.localizacao);
        });

        return false;
    }

    isUsuarioAutorizado() {
        return (
            this.usuario &&
            this.usuario.roles &&
            this.usuario.roles.findIndex((role) => role.toUpperCase() === 'SECRETARIO') !== -1
        );
    }

    isUsuarioAdmin() {
        if (this.rootStore && this.rootStore.parametroStore) {
            const usuariosAutorizados = this.rootStore.parametroStore.getParametro('USUARIOS_ADMIN');

            if (usuariosAutorizados && usuariosAutorizados.valor) {
                const usuariosAutorizadosArray = usuariosAutorizados.valor.split(/[,;]/); // Valores separados por ',' ou ';'
                return (
                    usuariosAutorizadosArray.includes(this.usuario.codigo) ||
                    usuariosAutorizadosArray.includes(this.usuario.cpf)
                );
            }
        }

        return false;
    }

    async configurarLocalizacaoSessaoPadrao(ojcs) {
        let proximaSessao;
        let ojcSessaoHoje;
        let ojcSessaoSelecionada = ojcs[0];

        for (const ojc of ojcs) {
            if (!ojcSessaoHoje) {
                let sessoes = await this.sessaoService.recuperarSessoesJulgamento(ojc.sigla);
                if (sessoes) {
                    let sessaoHoje = sessoes.find((sessao) => sessao.hoje);
                    if (sessaoHoje) {
                        ojcSessaoHoje = ojc;
                        ojcSessaoSelecionada = ojc;
                    } else {
                        let proxima = sessoes.find((sessao) => sessao.proxima);
                        if (
                            proxima &&
                            (!proximaSessao ||
                                moment(proxima.prazos.dataInicioSessaoPresencial, 'DD-MM-YYYY-HH-mm-ss').isBefore(
                                    moment(proximaSessao.prazos.dataInicioSessaoPresencial, 'DD-MM-YYYY-HH-mm-ss'),
                                    'day'
                                ))
                        ) {
                            proximaSessao = proxima;
                            ojcSessaoSelecionada = ojc;
                        }
                    }
                }
            }
        }
        this.configurarLocalizacao(ojcSessaoSelecionada);
    }

    configurarLocalizacao(orgaoJulgadorColegiado) {
        if (orgaoJulgadorColegiado) {
            let usuario = toJS(this.usuario); // Clonando o usuário para que as reactions executem corretamente
            usuario.descricaoLocalizacao = orgaoJulgadorColegiado.descricao;
            usuario.localizacao = orgaoJulgadorColegiado.sigla;
            this.usuario = usuario;
        }
    }

    async executarRequisicaoHttp(request) {
        let resposta;

        await fetch(request)
            .then(function (response) {
                if (response.ok) {
                    resposta = response.json();
                } else {
                    throw new Error(`Falha ao requisitar ${request.url} (${response.status})`, {
                        // @ts-ignore
                        cause: response.error
                    });
                }
            })
            .catch(function (error) {
                throw error;
            });

        return resposta;
    }
}
